import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { DataGrid } from "@mui/x-data-grid";
import { useEffect, useState, useRef } from "react";
import { Get } from "../../Common/WebRequest";
import { getStorageItem, getToDay, slice } from "../../Common/Utility";
import EditToolbar from "./EditToolbar";
import columnsBuilder from "./columnsBuilder";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SuccessIcon from "@mui/icons-material/CheckCircleOutline";
import { blue } from "@mui/material/colors";
import { Typography, Alert } from "@mui/material";

EditToolbar.propTypes = {
  setRowModesModel: PropTypes.func.isRequired,
  exportFileName: PropTypes.string,
};

const GRID_STYLES = {
  width: "100%",
  height: "850px",
  "& .Mui-even": { backgroundColor: "#FCFFEF" },
  "& .Mui-odd": { backgroundColor: "#FCFCFC" },
  "& .MuiDataGrid-cell:hover": { backgroundColor: "#80B3FF" },
  "&& .MuiDataGrid-row:hover": { backgroundColor: "#cbe0ff" },
};

const INITIAL_GRID_STATE = {
  pagination: {
    paginationModel: { pageSize: 100, page: 0 },
  },
  filter: {
    filterModel: {
      items: [{ field: "actionDescription", operator: "contains", value: "" }],
    },
  },
};

export default function AuditGrid() {
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [auditLogData, setAuditLogData] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [userName, setUserName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const dateFrom = useRef();
  const dateTo = useRef();

  const columns = columnsBuilder({});
  const exportFileName = "AuditLogData";

  useEffect(() => {
    const initializeData = async () => {
      const user = await getStorageItem("login", "userName");
      setUserName(user);
      await loadLogData();
    };

    initializeData();
  }, [userName]);

  const loadLogData = async () => {
    setError("");
    setAuditLogData([]);
    setIsLoading(true);

    if (!userName) {
      setIsLoading(false);
      return;
    }

    try {
      const res = await Get(
        `AuditLog/${userName}/${dateFrom.current.value}/${dateTo.current.value}`
      );

      if (res.isSuccess) {
        setAuditLogData(res.result);
      } else if (res.result && !res.result.includes("200/No Results")) {
        setError(`${res.Status}/${res.Error}/${res.ErrorDetail}`);
      }
    } catch (err) {
      setError(`Failed to load audit log data: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleShow = async (e) => {
    e.preventDefault();
    await loadLogData();
  };

  const renderMessage = () => {
    if (error) {
      return (
        <Alert severity="error" icon={<HighlightOffIcon />}>
          {slice(error, 100, "...")}
        </Alert>
      );
    }

    if (successMsg) {
      return (
        <Alert severity="success" icon={<SuccessIcon />}>
          {slice(successMsg, 100, "...")}
        </Alert>
      );
    }

    return null;
  };

  return (
    <Box sx={GRID_STYLES}>
      <div className="App" style={{ backgroundColor: "white" }}>
        <Typography variant="h5" align="left" color={blue[500]}>
          Audit Logs
        </Typography>
      </div>
      <br />

      <form className="d-flex gap-3 align-items-center mb-3">
        <div className="d-flex align-items-center">
          <label htmlFor="from" className="me-2">
            Von
          </label>
          <input
            className="form-control"
            id="from"
            type="date"
            ref={dateFrom}
            defaultValue={getToDay()}
          />
        </div>

        <div className="d-flex align-items-center">
          <label htmlFor="to" className="me-2">
            Zum
          </label>
          <input
            className="form-control"
            id="to"
            type="date"
            ref={dateTo}
            defaultValue={getToDay()}
          />
        </div>

        <button className="btn btn-primary" onClick={handleShow}>
          Vorschu
        </button>
      </form>

      {renderMessage()}

      <DataGrid
        loading={isLoading}
        rowSelection={false}
        density="compact"
        rows={auditLogData}
        columns={columns}
        editMode="row"
        initialState={INITIAL_GRID_STATE}
        rowModesModel={rowModesModel}
        slots={{ toolbar: EditToolbar }}
        slotProps={{
          toolbar: {
            setAuditLogData,
            setRowModesModel,
            exportFileName,
          },
        }}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd"
        }
      />
    </Box>
  );
}
